/**
 * AdminEmail.jsx — two admin pages bundled together:
 *   1. <AdminEmailTemplates /> — edit subject + body of all 9 transactional emails
 *      + global sender config (from name, from email, reply-to).
 *   2. <AdminEmailOutbox />    — view queued / sent / failed email rows with filters.
 */
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api from "../lib/api";
import AdminEmailBroadcast from "../components/admin/AdminEmailBroadcast";

const StatusPill = ({ s }) => {
  const cls = {
    queued: "bg-amber-100 text-amber-800",
    sent: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
    skipped: "bg-neutral-200 text-neutral-600",
  }[s] || "bg-neutral-100 text-neutral-600";
  return <span className={`text-[10px] px-1.5 py-0.5 font-mono uppercase ${cls}`}>{s}</span>;
};

// =====================================================================
// (1) Email Templates Editor
// =====================================================================
export function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({ from_name: "", from_email: "", reply_to: "" });
  const [selectedKey, setSelectedKey] = useState(null);
  const [editing, setEditing] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  // Top-level tab — Templates (transactional) vs Broadcast (one-off mass mail).
  const [tab, setTab] = useState("templates");

  const reload = async () => {
    const [t, s] = await Promise.all([
      api.get("/admin/email-templates"),
      api.get("/admin/email-templates/settings"),
    ]);
    setTemplates(t.data);
    setSettings(s.data);
  };

  useEffect(() => { reload(); }, []);

  const select = (key) => {
    const t = templates.find((x) => x.key === key);
    setSelectedKey(key);
    setEditing({ subject: t.subject, body_html: t.body_html });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${selectedKey}`, editing);
      toast.success("Template saved.");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset this template back to the default?")) return;
    try {
      await api.delete(`/admin/email-templates/${selectedKey}`);
      toast.success("Reset to default.");
      reload();
      setEditing(null);
      setSelectedKey(null);
    } catch (err) { toast.error("Reset failed"); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/email-templates/settings", settings);
      toast.success("Sender config saved. Next email tick (30s) will use the new From line.");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSavingSettings(false); }
  };

  const selected = templates.find((t) => t.key === selectedKey);

  return (
    <div className="max-w-6xl" data-testid="admin-email-templates">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · EMAIL CMS</div>
        <h1 className="text-4xl font-black tracking-tighter">Email templates</h1>
        <p className="text-sm text-neutral-500 mt-1">Edit every transactional email the platform sends. Merge variables like <code>{"{{name}}"}</code> are substituted per recipient before send.</p>
      </div>

      {/* Tab strip — Templates (transactional) vs Send broadcast (one-off mass mail) */}
      <div className="flex items-center gap-1 border-b-2 border-black mb-6">
        <button onClick={() => setTab("templates")} data-testid="email-tab-templates" className={`px-5 py-3 text-sm font-bold border-b-2 -mb-0.5 ${tab === "templates" ? "border-mango text-black" : "border-transparent text-neutral-500 hover:text-black"}`}>Transactional templates</button>
        <button onClick={() => setTab("broadcast")} data-testid="email-tab-broadcast" className={`px-5 py-3 text-sm font-bold border-b-2 -mb-0.5 ${tab === "broadcast" ? "border-mango text-black" : "border-transparent text-neutral-500 hover:text-black"}`}>📣 Send broadcast</button>
      </div>

      {tab === "broadcast" ? (
        <AdminEmailBroadcast />
      ) : (
        <>
      {/* Sender config */}
      <div className="bg-black text-white p-5 mb-6 border-2 border-black" data-testid="email-sender-config">
        <div className="overline text-mango mb-3">SENDER CONFIG (applies to ALL emails)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-white">From name</Label>
            <Input data-testid="from-name" value={settings.from_name || ""} onChange={(e) => setSettings({ ...settings, from_name: e.target.value })} className="bg-white text-black rounded-none" placeholder="oodel." />
            <p className="text-xs text-neutral-400 mt-1">Shown as the sender in customer inboxes.</p>
          </div>
          <div>
            <Label className="text-white">From email</Label>
            <Input data-testid="from-email" value={settings.from_email || ""} onChange={(e) => setSettings({ ...settings, from_email: e.target.value })} className="bg-white text-black rounded-none" placeholder="noreply@oodel.co" />
            <p className="text-xs text-neutral-400 mt-1">Must match a verified Resend domain. Defaults to <code>noreply@oodel.co</code> once your domain is verified.</p>
          </div>
          <div>
            <Label className="text-white">Reply-to</Label>
            <Input data-testid="reply-to" value={settings.reply_to || ""} onChange={(e) => setSettings({ ...settings, reply_to: e.target.value })} className="bg-white text-black rounded-none" placeholder="hello@oodel.co" />
            <p className="text-xs text-neutral-400 mt-1">Replies bounce here. Optional but recommended.</p>
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={saveSettings} disabled={savingSettings} className="bg-mango hover:bg-mango/90 text-white rounded-none" data-testid="save-sender">{savingSettings ? "Saving…" : "Save sender config"}</Button>
        </div>
      </div>

      {/* Template list + editor */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-1">
          {templates.map((t) => (
            <button
              key={t.key}
              onClick={() => select(t.key)}
              className={`w-full text-left p-3 border ${selectedKey === t.key ? 'border-mango bg-mango/5' : 'border-neutral-200 bg-white hover:border-neutral-400'}`}
              data-testid={`tpl-${t.key}`}
            >
              <div className="font-bold text-sm">{t.label}</div>
              <div className="text-xs text-neutral-500 mt-1">{t.description}</div>
              <div className="flex items-center gap-2 mt-2">
                <code className="text-[10px] bg-neutral-100 px-1 py-0.5">{t.key}</code>
                {t.is_overridden ? <span className="text-[10px] text-mango font-bold">CUSTOM</span> : <span className="text-[10px] text-neutral-400">default</span>}
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-8">
          {selected && editing ? (
            <div className="space-y-3 bg-white border-2 border-black p-5 hard-shadow" data-testid="tpl-editor">
              <div className="overline">EDIT TEMPLATE · {selected.key}</div>
              <p className="text-xs text-neutral-500">{selected.description}</p>
              <div>
                <Label>Available merge variables (click to copy)</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(selected.merge_vars || []).map((v) => (
                    <button
                      key={v}
                      type="button"
                      className="bg-neutral-100 hover:bg-neutral-200 text-xs px-2 py-1 font-mono"
                      onClick={() => { navigator.clipboard.writeText(`{{${v}}}`); toast.success(`Copied {{${v}}}`); }}
                    >{`{{${v}}}`}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Subject</Label>
                <Input data-testid="tpl-subject" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className="rounded-none" />
              </div>
              <div>
                <Label>Body (HTML)</Label>
                <textarea
                  data-testid="tpl-body"
                  value={editing.body_html}
                  onChange={(e) => setEditing({ ...editing, body_html: e.target.value })}
                  rows={16}
                  className="w-full border border-neutral-300 p-3 font-mono text-xs"
                />
              </div>
              {/* Live preview */}
              <details className="border border-neutral-200 p-3">
                <summary className="cursor-pointer text-sm font-bold">Live preview ▼</summary>
                <div className="mt-3 border border-neutral-300 p-4 bg-neutral-50">
                  <div className="text-xs text-neutral-500 mb-2">Subject: <b>{editing.subject}</b></div>
                  <div dangerouslySetInnerHTML={{ __html: editing.body_html }} />
                </div>
              </details>
              <div className="flex gap-2 pt-2 border-t">
                <Button onClick={save} disabled={saving} className="bg-mango hover:bg-mango/90 text-white rounded-none" data-testid="tpl-save">{saving ? "Saving…" : "Save template"}</Button>
                {selected.is_overridden && (
                  <Button onClick={reset} variant="outline" className="rounded-none text-red-600 border-red-600" data-testid="tpl-reset">Reset to default</Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-neutral-500 italic bg-white p-12 text-center border border-dashed border-neutral-300">Pick a template on the left to edit.</div>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
}

// =====================================================================
// (2) Email Outbox viewer
// =====================================================================
export function AdminEmailOutbox() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("all");
  const [kindTab, setKindTab] = useState("all");  // "all" | "system" | "admin"
  const [busy, setBusy] = useState(null);
  const [showBody, setShowBody] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const parts = [];
    if (filter !== "all") parts.push(`status=${filter}`);
    if (kindTab === "admin") parts.push(`kind=admin_to_patron`);
    const q = parts.length ? `?${parts.join("&")}` : "";
    try {
      const [r, s] = await Promise.all([
        api.get(`/admin/email-outbox${q}`),
        api.get("/admin/email-outbox/stats"),
      ]);
      // For the "system" tab we filter client-side (exclude admin_to_patron) so we
      // don't need a backend-side "kind != X" param. The dataset is small enough
      // that this is fine.
      const filtered = kindTab === "system"
        ? (r.data || []).filter((row) => row.kind !== "admin_to_patron")
        : r.data;
      setRows(filtered); setStats(s.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter, kindTab]); // eslint-disable-line

  const retry = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/email-outbox/${id}/retry`);
      toast.success("Re-queued. Will retry within 30s.");
      load();
    } catch (err) { toast.error("Retry failed"); }
    finally { setBusy(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this email row permanently?")) return;
    try {
      await api.delete(`/admin/email-outbox/${id}`);
      load();
    } catch (err) { toast.error("Delete failed"); }
  };

  const viewBody = async (id) => {
    try {
      const r = await api.get(`/admin/email-outbox/${id}`);
      setShowBody(r.data);
    } catch (err) { toast.error("Couldn't load"); }
  };

  const StatCard = ({ label, value, k, active }) => (
    <button
      onClick={() => setFilter(k)}
      className={`bg-white border-2 ${active ? 'border-black hard-shadow' : 'border-neutral-200'} p-4 text-left`}
      data-testid={`outbox-filter-${k}`}
    >
      <div className="overline">{label}</div>
      <div className="text-3xl font-black">{value || 0}</div>
    </button>
  );

  return (
    <div className="max-w-6xl" data-testid="admin-email-outbox">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">SECTION · EMAIL OUTBOX</div>
        <h1 className="text-4xl font-black tracking-tighter">Outgoing emails</h1>
        <p className="text-sm text-neutral-500 mt-1">Every email the system queued. Failed rows show the Resend error; click retry to re-attempt.</p>
      </div>

      {/* Source tabs — split system-generated emails from admin-composed
          messages (sent via the per-user "Send email" modal on All Users). */}
      <div className="flex items-center gap-1 mb-4 border-b border-neutral-200" data-testid="outbox-source-tabs">
        {[
          { v: "all", l: "All emails" },
          { v: "system", l: "System-generated" },
          { v: "admin", l: "Sent by admin to users" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setKindTab(t.v)}
            data-testid={`outbox-tab-${t.v}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px ${kindTab === t.v ? "border-black text-black" : "border-transparent text-neutral-500 hover:text-black"}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="All" value={stats.total} k="all" active={filter === "all"} />
        <StatCard label="Queued" value={stats.queued} k="queued" active={filter === "queued"} />
        <StatCard label="Sent" value={stats.sent} k="sent" active={filter === "sent"} />
        <StatCard label="Failed" value={stats.failed} k="failed" active={filter === "failed"} />
        <StatCard label="Skipped" value={stats.skipped} k="skipped" active={filter === "skipped"} />
      </div>

      <div className="space-y-1">
        {!loading && rows.length === 0 && <div className="bg-white p-12 text-center text-neutral-500 italic border border-dashed">No emails matching this filter.</div>}
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-neutral-200 p-3 text-sm" data-testid={`outbox-row-${r.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusPill s={r.status} />
                  <code className="text-[10px] bg-neutral-100 px-1 py-0.5">{r.kind}</code>
                  <span className="font-medium">{r.to}</span>
                </div>
                <div className="text-xs text-neutral-600 mt-1 truncate">{r.subject}</div>
                <div className="text-[10px] text-neutral-400">{r.created_at?.slice(0, 16)} {r.sent_at && <> · sent {r.sent_at.slice(11, 16)}</>}</div>
                {r.error && <div className="text-xs text-red-600 mt-1" data-testid={`error-${r.id}`}>⚠ {r.error}</div>}
              </div>
              <div className="flex gap-2 text-xs">
                <button onClick={() => viewBody(r.id)} className="text-neutral-600 hover:text-black" data-testid={`view-${r.id}`}>View</button>
                {(r.status === "failed" || r.status === "skipped") && (
                  <button onClick={() => retry(r.id)} disabled={busy === r.id} className="text-mango hover:text-mango/80 font-bold" data-testid={`retry-${r.id}`}>{busy === r.id ? "..." : "Retry"}</button>
                )}
                <button onClick={() => del(r.id)} className="text-red-600 hover:text-red-800" data-testid={`del-${r.id}`}>×</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Body preview modal */}
      {showBody && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowBody(null)} data-testid="body-preview">
          <div className="bg-white max-w-2xl w-full p-6 border-2 border-black max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs text-neutral-500 mb-2">To: <b>{showBody.to}</b> · Kind: <code>{showBody.kind}</code></div>
            <h2 className="text-xl font-bold mb-4">{showBody.subject}</h2>
            <div className="border border-neutral-200 p-4 bg-neutral-50" dangerouslySetInnerHTML={{ __html: showBody.body_html || "(empty)" }} />
            <Button onClick={() => setShowBody(null)} variant="outline" className="mt-4 rounded-none">Close</Button>
          </div>
        </div>
      )}
    </div>
  );
}
